"use client";

import React, { useState, useEffect } from "react";
import { useGraphStore } from "@/store/graph-store";
import { fetchNodesAction } from "@/actions/graph-actions";
import { Input } from "@/components/ui/form-elements";
import { Badge } from "@/components/ui/feedback";
import { Card, CardContent } from "@/components/ui/card";
import {
  Target,
  Users,
  FolderGit,
  Landmark,
  ShieldAlert,
  FileText,
  Truck,
  Lightbulb,
  Search,
  GripVertical,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Mapping of types to Lucide Icons
const iconMap: Record<string, any> = {
  strategy: Target,
  people: Users,
  work: FolderGit,
  finance: Landmark,
  operations: Truck,
  risk: ShieldAlert,
  documents: FileText,
  innovation: Lightbulb,
};

// Color accents for node templates
const colorMap: Record<string, string> = {
  strategy: "bg-category-strategy",
  people: "bg-category-people",
  work: "bg-category-work",
  finance: "bg-category-finance",
  operations: "bg-category-operations",
  risk: "bg-category-risk",
  documents: "bg-category-documents",
  innovation: "bg-category-innovation",
};

interface TemplateItem {
  type: string;
  label: string;
  description: string;
}

const TEMPLATE_ITEMS: TemplateItem[] = [
  {
    type: "strategy",
    label: "Strategy Goal",
    description: "Define strategic objectives and KRs",
  },
  {
    type: "work",
    label: "Work Project",
    description: "Track execution progress and milestones",
  },
  {
    type: "people",
    label: "People Agent",
    description: "Represent human operators or AI agents",
  },
  {
    type: "finance",
    label: "Finance Budget",
    description: "Manage costs, revenues, and expenses",
  },
  {
    type: "operations",
    label: "Operations Item",
    description: "Track supply chain and suppliers",
  },
  {
    type: "risk",
    label: "Risk Blocker",
    description: "Flag blockers, high risks, or issues",
  },
  {
    type: "documents",
    label: "Document Node",
    description: "Upload PDFs, contracts, and references",
  },
  {
    type: "innovation",
    label: "Innovation Idea",
    description: "Collect brainwaves and experimental ideas",
  },
];

function mapDbTypeToCategory(dbType: string): string {
  switch (dbType) {
    case "goal":
      return "strategy";
    case "people":
    case "agent":
      return "people";
    case "project":
    case "milestone":
    case "task":
    case "lead":
      return "work";
    case "invoice":
    case "payment":
      return "finance";
    case "supplier":
    case "asset":
      return "operations";
    case "risk":
      return "risk";
    case "documents":
      return "documents";
    case "initiative":
      return "innovation";
    default:
      return dbType;
  }
}

export function ElementPanel() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"templates" | "existing">(
    "templates",
  );
  const [existingNodes, setExistingNodes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { nodes } = useGraphStore();

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 250);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Load existing nodes from database when switching tabs or query changes
  useEffect(() => {
    if (activeTab === "existing") {
      const loadExisting = async () => {
        setIsLoading(true);
        try {
          const res = await fetchNodesAction();
          setExistingNodes(res || []);
        } catch (err) {
          console.error("Failed to load existing nodes in library panel:", err);
        } finally {
          setIsLoading(false);
        }
      };
      loadExisting();
    }
  }, [activeTab]);

  // Handle Drag Start
  const handleDragStart = (
    event: React.DragEvent,
    type: string,
    label: string,
    isTemplate: boolean,
    existingId?: string,
  ) => {
    const dragData = {
      type,
      label,
      isTemplate,
      existingId,
    };
    event.dataTransfer.setData(
      "application/reactflow",
      JSON.stringify(dragData),
    );
    event.dataTransfer.effectAllowed = "move";
  };

  // Filter templates
  const filteredTemplates = TEMPLATE_ITEMS.filter(
    (item) =>
      item.label.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
      item.type.toLowerCase().includes(debouncedQuery.toLowerCase()),
  );

  // Filter existing nodes
  const filteredExisting = existingNodes.filter((node) => {
    const nameMatch = node.name
      .toLowerCase()
      .includes(debouncedQuery.toLowerCase());
    const typeMatch = node.type
      .toLowerCase()
      .includes(debouncedQuery.toLowerCase());
    return nameMatch || typeMatch;
  });

  return (
    <div className="flex h-full w-80 flex-col border-r border-border bg-card text-foreground select-none">
      {/* Header and Search */}
      <div className="p-4 border-b border-border/60 space-y-3">
        <div>
          <h2 className="text-base font-bold text-text-primary">
            Element Library
          </h2>
          <p className="text-xs text-text-secondary">
            Drag elements onto the canvas to map relationships
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search elements..."
            className="pl-9 h-9 text-sm"
          />
        </div>

        {/* Tab Navigation */}
        <div className="flex rounded-lg bg-card-muted p-0.5 text-xs font-semibold">
          <button
            onClick={() => setActiveTab("templates")}
            className={cn(
              "flex-1 py-1.5 rounded-md text-center transition-colors cursor-pointer",
              activeTab === "templates"
                ? "bg-card text-text-primary shadow-sm"
                : "text-text-secondary hover:text-text-primary",
            )}
          >
            Create New
          </button>
          <button
            onClick={() => setActiveTab("existing")}
            className={cn(
              "flex-1 py-1.5 rounded-md text-center transition-colors cursor-pointer",
              activeTab === "existing"
                ? "bg-card text-text-primary shadow-sm"
                : "text-text-secondary hover:text-text-primary",
            )}
          >
            Existing Nodes
          </button>
        </div>
      </div>

      {/* Scroller Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === "templates" ? (
          <div className="space-y-2">
            {filteredTemplates.length > 0 ? (
              filteredTemplates.map((item) => {
                const Icon = iconMap[item.type] || FolderGit;
                return (
                  <div
                    key={item.type}
                    draggable
                    onDragStart={(e) =>
                      handleDragStart(e, item.type, item.label, true)
                    }
                    className="group relative flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-card-muted hover:border-primary-500/50 transition-all cursor-grab active:cursor-grabbing shadow-[var(--shadow-card)]"
                  >
                    {/* Left Accent line */}
                    <div
                      className={cn(
                        "absolute left-0 top-0 bottom-0 w-1 rounded-l-lg",
                        colorMap[item.type],
                      )}
                    />

                    {/* Icon container */}
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-card-muted border border-border/50 text-text-secondary">
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Metadata */}
                    <div className="flex-1 min-w-0 ml-1">
                      <h4 className="text-xs font-bold text-text-primary truncate">
                        {item.label}
                      </h4>
                      <p className="text-[10px] text-text-secondary truncate mt-0.5">
                        {item.description}
                      </p>
                    </div>

                    {/* Drag Handle indicator */}
                    <div className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="h-4 w-4" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-xs text-text-secondary">
                No templates match your search.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {isLoading ? (
              <div className="flex flex-col gap-2 py-4">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="h-14 rounded-lg bg-card-muted/50 animate-pulse border border-border/30"
                  />
                ))}
              </div>
            ) : filteredExisting.length > 0 ? (
              filteredExisting.map((node) => {
                const category = mapDbTypeToCategory(node.type);
                const Icon = iconMap[category] || FolderGit;
                const isOnCanvas = nodes.some((n) => n.id === node.id);

                return (
                  <div
                    key={node.id}
                    draggable={!isOnCanvas}
                    onDragStart={(e) =>
                      handleDragStart(e, node.type, node.name, false, node.id)
                    }
                    className={cn(
                      "group relative flex items-center gap-3 p-3 rounded-lg border transition-all shadow-[var(--shadow-card)]",
                      isOnCanvas
                        ? "border-border/30 bg-card-muted/30 opacity-60 cursor-not-allowed"
                        : "border-border bg-card hover:bg-card-muted hover:border-primary-500/50 cursor-grab active:cursor-grabbing",
                    )}
                    title={
                      isOnCanvas
                        ? "Node is already placed on the canvas"
                        : "Drag to place on canvas"
                    }
                  >
                    {/* Left Accent line */}
                    <div
                      className={cn(
                        "absolute left-0 top-0 bottom-0 w-1 rounded-l-lg",
                        colorMap[category] || "bg-border-strong",
                      )}
                    />

                    {/* Icon container */}
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-card-muted border border-border/50 text-text-secondary">
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Metadata */}
                    <div className="flex-1 min-w-0 ml-1">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-xs font-bold text-text-primary truncate">
                          {node.name}
                        </h4>
                        <Badge
                          variant={category as any}
                          className="text-[8px] px-1 py-0 scale-90 origin-left"
                        >
                          {category}
                        </Badge>
                      </div>
                      <p className="text-[10px] text-text-secondary truncate mt-0.5">
                        {isOnCanvas
                          ? "Already on canvas"
                          : "Ready to be dragged"}
                      </p>
                    </div>

                    {/* Drag Handle or Plus icon */}
                    {!isOnCanvas && (
                      <div className="text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-xs text-text-secondary">
                {searchQuery
                  ? "No existing nodes found matching your query."
                  : "No nodes available in the repository."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
